/* global Sk, ace */
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { I18n } from 'react-redux-i18n';
import MenuItem from 'react-bootstrap/lib/MenuItem';
import DropdownButton from 'react-bootstrap/lib/DropdownButton';
import request from 'superagent';

import Script from 'react-load-script';
import AceEditor from 'react-ace';
import Toolbox from './Toolbox';
import Projects from './Projects';
import SaveDialog from './SaveDialog';
import ShareDialog from './ShareDialog';
import PendingDialog from './PendingDialog';
import 'brace/theme/xcode';
import 'brace/theme/twilight';
import 'brace/mode/plain_text';
import 'brace/ext/language_tools';
import { receiveProject, saveProject } from '@actions/projects';
import { getCourse } from '@actions/courses';
import { finishLoad } from '@actions/loader';
import { setLauncherGuideVisibility } from '@actions/utils';
import GuideButton from '../GuideButton';
import { projectApi } from '@api/project';

const { Range } = ace.acequire('ace/range');

class Ace extends React.PureComponent {
  state = {
    code: '',
    result: '',
    isLimited: false,
    loadedMin: false,
    loadedStdlib: false,
    executing: false,
    toggled: true,
    showProjectModal: false,
    showShareDialog: false,
    showPendingDialog: false,
    showSaveDialog: false,
  };

  constructor(props, context) {
    super(props, context);
    this.defineAceMode();

    this.outText = [];
    this.socket = null;
  }

  prepareSkulpt = () => {
    const { finishLoad } = this.props;

    Sk.configure({
      output: this.print,
      read: filename => {
        if (
          Sk.builtinFiles === undefined ||
          Sk.builtinFiles.files[filename] === undefined
        ) {
          throw Error(`File not found: '${filename}'`);
        }

        return Sk.builtinFiles.files[filename];
      },
      inputfunTakesPrompt: true,
      inputfun: function(prompt) {
        return window.prompt(prompt);
      },
      __future__: Sk.python3,
    });
    finishLoad();
  };

  onChange = e => {
    this.setState({ code: e });
    this.clearMarkers();
  };

  print = text => {
    this.outText.push(text);
    this.forceUpdate();
    this.refs.console.editor.scrollToLine(this.outText.length);
  };

  highlightLine = (lineno, error) => {
    const session = this.refs.ace.editor.getSession();
    if (this.highlight) session.removeMarker(this.highlight.id);
    this.highlight = {
      id: session.addMarker(
        new Range(lineno, 0, lineno, 1),
        error ? 'errorMarker' : 'executingMarker',
        'fullLine',
        true
      ),
    };
    this.forceUpdate();
  };

  executionHandler = susp => {
    this.highlightLine(susp.child.$lineno > 0 ? susp.child.$lineno - 1 : 0);
    if (Sk.hardInterrupt === true) {
      const { socket } = this.props;
      socket.emit('stopScript');
      throw new Sk.builtin.SystemExit(
        'Aborted execution',
        0,
        susp.child.$lineno || 0
      );
    } else {
      return null; // should perform default action
    }
  };

  tryParse(data) {
    try {
      return JSON.parse(data);
    } catch (e) {
      return {
        error:
          'Unable to parse data.\n' +
          'This is not your fault! Please report if you encountered this error.\n' +
          'The cause of the error is located',
        result: false,
      };
    }
  }

  execute = code => {
    const { socket, isUserOnline } = this.props;
    if (!socket || !isUserOnline) {
      this.props.showLauncherGuide();
      return;
    }

    socket.off('task_processed');
    socket.on('task_processed', data => {
      const pythonTasks = window.pythonTasks;
      if (!data)
        throw new Sk.builtin.Exception('Task was processed without any data');
      data = this.tryParse(data);
      if (data.taskId === 'stopScriptPy') return;
      const resolve = pythonTasks[data.taskId];
      if (resolve) {
        resolve(data);
        delete pythonTasks[data.taskId];
      } else {
        throw new Sk.builtin.Exception(
          'Cannot find resolve function for delayed task resolving'
        );
      }
    });
    this.setState({ executing: true });
    this.outText = [];
    this.refs.ace.editor.getSession().setAnnotations([]);

    Sk.hardInterrupt = false;
    try {
      socket.emit('startScript', () => {
        Sk.misceval
          .asyncToPromise(
            () => Sk.importMainWithBody('<stdin>', false, code, true),
            { '*': this.executionHandler }
          )
          .then(
            () => {
              if (socket) {
                socket.emit('stopScript');
              }
              if (this.highlight) {
                this.refs.ace.editor
                  .getSession()
                  .removeMarker(this.highlight.id);
              }
              this.setState({ executing: false });
            },
            error => {
              if (this.highlight) {
                this.refs.ace.editor
                  .getSession()
                  .removeMarker(this.highlight.id);
              }

              if (error.traceback && error.traceback.length > 0) {
                const [loc, ...tb] = error.traceback.reverse();
                let stackTrace = error.toString();
                tb.forEach(({ filename, lineno }) => {
                  stackTrace += `\n File '${filename}' on line ${lineno}`;
                });
                this.print(stackTrace);
                this.highlightLine(loc.lineno - 1, true);
                this.refs.ace.editor.getSession().setAnnotations([
                  {
                    row: loc.lineno - 1,
                    column: loc.colno,
                    text: error.toString(),
                    type: 'error', // also warning and information
                  },
                ]);
              } else {
                this.print(`${error}\n`);
              }

              this.setState({ executing: false });
            }
          );
      });
    } catch (e) {
      this.setState({ result: e.toString() });
      this.setState({ executing: false });
    }
  };

  buttonClick = () => {
    projectApi.runProject(this.state.code);
    this.execute(this.state.code);
  };

  stopButtonClick = () => {
    Sk.hardInterrupt = true;
    this.setState({ executing: false });
  };

  clearMarkers = () => {
    this.refs.ace.editor.getSession().setAnnotations([]);
    if (this.highlight)
      this.refs.ace.editor.getSession().removeMarker(this.highlight.id);
  };

  clearConsole = () => {
    this.outText = [];
    this.clearMarkers();
    this.forceUpdate();
  };

  toggleProjectModal = () => {
    this.setState({ showProjectModal: !this.state.showProjectModal });
  };

  toggleShareModal = () => {
    this.setState({ showShareDialog: !this.state.showShareDialog });
  };

  togglePendingModal = () => {
    this.setState({ showPendingDialog: !this.state.showPendingDialog });
  };

  toggleSaveDialog = () => {
    this.setState({ showSaveDialog: !this.state.showSaveDialog });
  };

  loadProject = project => {
    this.refs.ace.editor.session.setValue(
      project.code != null ? project.code : ''
    );
    this.props.loadProject(project);
  };

  saveProject = (newProject, name) => {
    const currentProject = this.props.currentProject;
    const projectToSave = {
      name: newProject || !currentProject ? name : currentProject.name,
      category: 'Projects',
      lang: 'python',
      code: this.state.code,
    };

    if (this.state.showSaveDialog) this.toggleSaveDialog();
    this.props.saveProject(projectToSave);
  };

  clearButtonClick = () => {
    const { socket } = this.props;
    if (socket) {
      socket.emit('clear');
    }
  };

  toggleLimits = () => {
    const { socket } = this.props;
    if (socket) {
      socket.emit('getLimits', {}, response => {
        const limited = !Boolean(JSON.parse(response));
        socket.emit('setLimits', limited, () => {
          this.setState({ isLimited: limited });
        });
      });
    }
  };

  spawnButtonClick = () => {
    const { socket } = this.props;
    if (socket) {
      socket.emit('spawn');
    }
  };

  componentWillMount() {
    window.pythonTasks = {};
  }

  componentDidMount() {
    window.addEventListener('keydown', this.handleKeydown);
    Object.keys(Array.prototype).forEach(key => delete Array.prototype[key]);
    this.interval = setInterval(() => {
      if (!this.state.showSaveDialog && this.state.code !== '') {
        this.saveProject(false);
      }
    }, 30000);

    const {
      location: {
        query: { id },
      },
    } = this.props;

    id &&
      request.get(`/project/xml/${id}`).then(data => {
        if (data && data.body)
          this.setState({
            code: data.body,
          });
      });
  }

  componentWillReceiveProps(newProps) {
    const { pending } = newProps;
    if (pending.length > 0 && !this.state.showPendingDialog) {
      this.togglePendingModal();
    }
  }

  componentDidUpdate() {
    this.props.finishLoad();
    const { socket } = this.props;
    if (socket) {
      socket.emit('getLimits', {}, response => {
        const limited = Boolean(JSON.parse(response));
        this.setState({ isLimited: limited });
      });
    }
  }

  componentWillUnmount() {
    this.props.clearProject();
    window.removeEventListener('keydown', this.handleKeydown);
    clearInterval(this.interval);
  }

  handleKeydown = event => {
    const ch = String.fromCharCode(event.which).toLowerCase();
    if ((event.ctrlKey || event.metaKey) && ch === 's') {
      event.preventDefault();
      if (this.props.currentProject) {
        this.saveProject(false);
      } else {
        this.toggleSaveDialog();
      }
    }
  };

  defineAceMode() {
    ace.define(
      'ace/mode/python_highlight_rules',
      [
        'require',
        'exports',
        'module',
        'ace/lib/oop',
        'ace/mode/text_highlight_rules',
      ],
      (acequire, exports, module) => {
        const oop = acequire('../lib/oop');
        const TextHighlightRules = acequire('./text_highlight_rules')
          .TextHighlightRules;

        const PythonHighlightRules = function() {
          const mcConstants = 'WEST|EAST|SOUTH|NORTH|UP|DOWN';
          /* ("AIR|STONE|GRASS|DIRT|COBBLESTONE|WOOD_PLANKS|SAPLING|" +
           "BEDROCK|WATER_FLOWING|WATER|WATER_STATIONARY|LAVA_FLOWING|LAVA|LAVA_STATIONARY|" +
           "SAND|GRAVEL|GOLD_ORE|IRON_ORE|COAL_ORE|WOOD|LEAVES|GLASS|LAPIS_LAZULI_ORE|" +
           "LAPIS_LAZULI_BLOCK|SANDSTONE|BED|COBWEB|GRASS_TALL|WOOL|FLOWER_YELLOW|" +
           "FLOWER_CYAN|MUSHROOM_BROWN|MUSHROOM_RED|GOLD_BLOCK|IRON_BLOCK|" +
           "STONE_SLAB_DOUBLE|STONE_SLAB|BRICK_BLOCK|TNT|BOOKSHELF|MOSS_STONE|" +
           "OBSIDIAN|TORCH|FIRE|STAIRS_WOOD|CHEST|DIAMOND_ORE|DIAMOND_BLOCK|" +
           "CRAFTING_TABLE|FARMLAND|FURNACE_INACTIVE|FURNACE_ACTIVE|DOOR_WOOD|" +
           "LADDER|STAIRS_COBBLESTONE|DOOR_IRON|REDSTONE_ORE|SNOW|ICE|SNOW_BLOCK|" +
           "CACTUS|CLAY|SUGAR_CANE|FENCE|GLOWSTONE_BLOCK|BEDROCK_INVISIBLE|" +
           "STONE_BRICK|GLASS_PANE|MELON|FENCE_GATE|");*/

          const mcFunction =
            'wait|postToChat|getBlock|getBlockData|getBlocks|getHeight|getPlayerId|' +
            'getPlayerIds|setBlock|setBlocks|setCuboid|buildArc|buildColumn|buildSphere|buildHome|buildWall|' +
            'buildHome|getDirection|getPitch|getPos|getRotation|setPos|move|lookTo|' +
            'turnLeft|turnRight|getDirection|setVelocity|getPitch|getPos|getRotation|setPos|moveForward' +
            '|move|lookTo|turn|turnLeft|turnRight|breakBlock|placeBlock|getBlockInFront|getPos' +
            '|rename|destroy|item|pickItem|putItem|useItem|takeItem|shootArrow|getInventory' +
            '|inventoryContains|dropItem|startDrawing|setParticle|stopDrawing|equip|unequip';

          const mcKeywords =
            'mc|world|player|entity|blocks|drone|drones|directions|rails|particles|weather|items|colors|creatures';
          const keywords =
            'and|as|assert|break|class|continue|def|del|elif|else|except|exec|' +
            'finally|for|from|global|if|import|in|is|lambda|not|or|pass|print|' +
            'raise|return|try|while|with|yield';

          const builtinConstants = `${mcConstants}|True|False|None|NotImplemented|Ellipsis|__debug__`;

          const builtinFunctions =
            `${mcFunction}|` +
            'abs|divmod|input|open|staticmethod|all|enumerate|int|ord|str|any|' +
            'eval|isinstance|pow|sum|basestring|execfile|issubclass|print|super|' +
            'binfile|iter|property|tuple|bool|filter|len|range|type|bytearray|' +
            'float|list|raw_input|unichr|callable|format|locals|reduce|unicode|' +
            'chr|frozenset|long|reload|vars|classmethod|getattr|map|repr|xrange|' +
            'cmp|globals|max|reversed|zip|compile|hasattr|memoryview|round|' +
            '__import__|complex|hash|min|set|apply|delattr|help|next|setattr|' +
            'buffer|dict|hex|object|slice|coerce|dir|id|oct|sorted|intern';
          const keywordMapper = this.createKeywordMapper(
            {
              'invalid.deprecated': 'debugger',
              'support.function': builtinFunctions,
              'constant.language': builtinConstants,
              mc_keyword: mcKeywords,
              keyword: keywords,
            },
            'identifier'
          );

          const strPre = '(?:r|u|ur|R|U|UR|Ur|uR)?';

          const decimalInteger = '(?:(?:[1-9]\\d*)|(?:0))';
          const octInteger = '(?:0[oO]?[0-7]+)';
          const hexInteger = '(?:0[xX][\\dA-Fa-f]+)';
          const binInteger = '(?:0[bB][01]+)';
          const integer = `(?:${decimalInteger}|${octInteger}|${hexInteger}|${binInteger})`;

          const exponent = '(?:[eE][+-]?\\d+)';
          const fraction = '(?:\\.\\d+)';
          const intPart = '(?:\\d+)';
          const pointFloat = `(?:(?:${intPart}?${fraction})|(?:${intPart}\\.))`;
          const exponentFloat = `(?:(?:${pointFloat}|${intPart})${exponent})`;
          const floatNumber = `(?:${exponentFloat}|${pointFloat})`;

          const stringEscape =
            '\\\\(x[0-9A-Fa-f]{2}|[0-7]{3}|[\\\\abfnrtv\'"]|U[0-9A-Fa-f]{8}|u[0-9A-Fa-f]{4})';

          this.$rules = {
            start: [
              {
                token: 'comment',
                regex: '#.*$',
              },
              {
                token: 'string', // multi line """ string start
                regex: `${strPre}"{3}`,
                next: 'qqstring3',
              },
              {
                token: 'string', // " string
                regex: `${strPre}"(?=.)`,
                next: 'qqstring',
              },
              {
                token: 'string', // multi line ''' string start
                regex: `${strPre}'{3}`,
                next: 'qstring3',
              },
              {
                token: 'string', // ' string
                regex: `${strPre}'(?=.)`,
                next: 'qstring',
              },
              {
                token: 'constant.numeric', // imaginary
                regex: `(?:${floatNumber}|\\d+)[jJ]\\b`,
              },
              {
                token: 'constant.numeric', // float
                regex: floatNumber,
              },
              {
                token: 'constant.numeric', // long integer
                regex: `${integer}[lL]\\b`,
              },
              {
                token: 'constant.numeric', // integer
                regex: `${integer}\\b`,
              },
              {
                token: keywordMapper,
                regex: '[a-zA-Z_$][a-zA-Z0-9_$]*\\b',
              },
              {
                token: 'keyword.operator',
                regex:
                  '\\+|\\-|\\*|\\*\\*|\\/|\\/\\/|%|<<|>>|&|\\||\\^|~|<|>|<=|=>|==|!=|<>|=',
              },
              {
                token: 'paren.lparen',
                regex: '[\\[\\(\\{]',
              },
              {
                token: 'paren.rparen',
                regex: '[\\]\\)\\}]',
              },
              {
                token: 'text',
                regex: '\\s+',
              },
            ],
            qqstring3: [
              {
                token: 'constant.language.escape',
                regex: stringEscape,
              },
              {
                token: 'string', // multi line """ string end
                regex: '"{3}',
                next: 'start',
              },
              {
                defaultToken: 'string',
              },
            ],
            qstring3: [
              {
                token: 'constant.language.escape',
                regex: stringEscape,
              },
              {
                token: 'string', // multi line ''' string end
                regex: "'{3}",
                next: 'start',
              },
              {
                defaultToken: 'string',
              },
            ],
            qqstring: [
              {
                token: 'constant.language.escape',
                regex: stringEscape,
              },
              {
                token: 'string',
                regex: '\\\\$',
                next: 'qqstring',
              },
              {
                token: 'string',
                regex: '"|$',
                next: 'start',
              },
              {
                defaultToken: 'string',
              },
            ],
            qstring: [
              {
                token: 'constant.language.escape',
                regex: stringEscape,
              },
              {
                token: 'string',
                regex: '\\\\$',
                next: 'qstring',
              },
              {
                token: 'string',
                regex: "'|$",
                next: 'start',
              },
              {
                defaultToken: 'string',
              },
            ],
          };
        };

        oop.inherits(PythonHighlightRules, TextHighlightRules);

        exports.PythonHighlightRules = PythonHighlightRules;
      }
    );

    ace.define(
      'ace/mode/folding/pythonic',
      [
        'require',
        'exports',
        'module',
        'ace/lib/oop',
        'ace/mode/folding/fold_mode',
      ],
      (acequire, exports, module) => {
        const oop = acequire('../../lib/oop');
        const BaseFoldMode = acequire('./fold_mode').FoldMode;

        const FoldMode = (exports.FoldMode = function(markers) {
          this.foldingStartMarker = new RegExp(
            `([\\[{])(?:\\s*)$|(${markers})(?:\\s*)(?:#.*)?$`
          );
        });
        oop.inherits(FoldMode, BaseFoldMode);

        (function() {
          this.getFoldWidgetRange = function(session, foldStyle, row) {
            const line = session.getLine(row);
            const match = line.match(this.foldingStartMarker);
            if (match) {
              if (match[1]) {
                return this.openingBracketBlock(
                  session,
                  match[1],
                  row,
                  match.index
                );
              }
              if (match[2]) {
                return this.indentationBlock(
                  session,
                  row,
                  match.index + match[2].length
                );
              }
              return this.indentationBlock(session, row);
            }
          };
        }.call(FoldMode.prototype));
      }
    );

    ace.define(
      'ace/mode/python',
      [
        'require',
        'exports',
        'module',
        'ace/lib/oop',
        'ace/mode/text',
        'ace/mode/python_highlight_rules',
        'ace/mode/folding/pythonic',
        'ace/range',
      ],
      (acequire, exports, module) => {
        const oop = acequire('../lib/oop');
        const TextMode = acequire('./text').Mode;
        const PythonHighlightRules = acequire('./python_highlight_rules')
          .PythonHighlightRules;
        const PythonFoldMode = acequire('./folding/pythonic').FoldMode;
        const Range = acequire('../range').Range;

        const Mode = function() {
          this.HighlightRules = PythonHighlightRules;
          this.foldingRules = new PythonFoldMode('\\:');
        };
        oop.inherits(Mode, TextMode);

        (function() {
          this.lineCommentStart = '#';

          this.getNextLineIndent = function(state, line, tab) {
            let indent = this.$getIndent(line);

            const tokenizedLine = this.getTokenizer().getLineTokens(
              line,
              state
            );
            const tokens = tokenizedLine.tokens;

            if (tokens.length && tokens[tokens.length - 1].type === 'comment') {
              return indent;
            }

            if (state === 'start') {
              const match = line.match(/^.*[{([:]\s*$/);
              if (match) {
                indent += tab;
              }
            }

            return indent;
          };

          const outdents = {
            pass: 1,
            return: 1,
            raise: 1,
            break: 1,
            continue: 1,
          };

          this.checkOutdent = function(state, line, input) {
            if (input !== '\r\n' && input !== '\r' && input !== '\n') {
              return false;
            }

            const tokens = this.getTokenizer().getLineTokens(line.trim(), state)
              .tokens;

            if (!tokens) {
              return false;
            }
            do {
              var last = tokens.pop();
            } while (
              last &&
              (last.type === 'comment' ||
                (last.type === 'text' && last.value.match(/^\s+$/)))
            );

            if (!last) {
              return false;
            }

            return last.type === 'keyword' && outdents[last.value];
          };

          this.autoOutdent = function(state, doc, row) {
            row += 1;
            const indent = this.$getIndent(doc.getLine(row));
            const tab = doc.getTabString();
            if (indent.slice(-tab.length) === tab) {
              doc.remove(
                new Range(row, indent.length - tab.length, row, indent.length)
              );
            }
          };

          this.$id = 'ace/mode/python';
        }.call(Mode.prototype));

        exports.Mode = Mode;
      }
    );
  }

  handleLoadMin = () => {
    this.setState({ loadedMin: true });
  };

  render() {
    return (
      <div className="col-xs-12 ide-container">
        <div className="container-fluid ide">
          <div
            className="row"
            style={{ height: '32px', backgroundColor: '#DEDEDE' }}
          >
            <div
              className="col-xs-6"
              style={{
                textOverflow: 'ellipsis',
                overflow: 'hidden',
                whiteSpace: 'nowrap',
              }}
            >
              <b
                style={{
                  textDecoration: 'underline',
                  margin: '6px 0 0 12px',
                  fontSize: '16px',
                }}
              >
                {' '}
                {this.props.currentProject != null
                  ? this.props.currentProject.name
                  : 'Untitled'}
              </b>
            </div>
            <div style={{ paddingTop: '4px' }} className="col-xs-6">
              <div className="dropdown" style={{ float: 'left' }}>
                <DropdownButton
                  bsSize="xsmall"
                  bsStyle="primary"
                  title={<i className="fa fa-file" aria-hidden="true" />}
                  noCaret
                  id="menu-dropdown"
                  className="menuButton btn btn-xs btn-default"
                >
                  <MenuItem eventKey="1" onClick={this.toggleProjectModal}>
                    Открыть
                  </MenuItem>
                  {this.props.currentProject &&
                  this.props.currentProject.readOnly ? (
                    <MenuItem
                      eventKey="2"
                      style={{ color: '#E3E3E3' }}
                      onClick={e => {
                        e.preventDefault();
                      }}
                    >
                      {I18n.t('gui.ide.save')}
                    </MenuItem>
                  ) : (
                    <MenuItem
                      eventKey="2"
                      onClick={e => {
                        this.saveProject(false);
                      }}
                    >
                      {' '}
                      {I18n.t('gui.ide.save')}{' '}
                    </MenuItem>
                  )}

                  <MenuItem
                    eventKey="3"
                    onClick={e => {
                      this.toggleSaveDialog();
                    }}
                  >
                    {I18n.t('gui.ide.saveAs')}
                  </MenuItem>

                  <MenuItem divider />
                  <MenuItem
                    eventKey="4"
                    onClick={() => {
                      this.saveProject(!this.props.currentProject);
                      this.toggleShareModal();
                    }}
                  >
                    {I18n.t('gui.ide.share')}
                  </MenuItem>

                  <MenuItem
                    eventKey="5"
                    onClick={() => {
                      this.togglePendingModal();
                    }}
                  >
                    {I18n.t('gui.ide.inbox')} (
                    <b>{this.props.pending ? this.props.pending.length : 0}</b>)
                  </MenuItem>
                </DropdownButton>
              </div>
              <button
                className="menuButton btn btn-xs  btn-primary"
                type="button"
                onClick={() => {
                  this.setState({ toggled: !this.state.toggled });
                }}
                title={I18n.t('gui.ide.documentation')}
              >
                <i className="fa fa-book" aria-hidden="true" />
              </button>
              <button
                onClick={this.buttonClick}
                title={I18n.t('gui.ide.run')}
                className={
                  this.state.code.length > 0
                    ? 'menuButton btn btn-xs btn-success'
                    : 'menuButton btn btn-xs btn-default'
                }
                disabled={this.state.executing || this.state.code.length === 0}
              >
                <i className="fa fa-play" aria-hidden="true" />
              </button>
              <button
                onClick={this.stopButtonClick}
                title={I18n.t('gui.ide.stop')}
                className={
                  this.state.executing
                    ? 'menuButton btn btn-xs btn-danger'
                    : 'menuButton btn btn-xs btn-default'
                }
                disabled={!this.state.executing}
              >
                <i className="fa fa-stop" aria-hidden="true" />
              </button>
              <button
                onClick={this.spawnButtonClick}
                title={I18n.t('gui.ide.toSpawn')}
                className={
                  !this.state.executing
                    ? 'menuButton btn btn-xs btn-info'
                    : 'menuButton btn btn-xs btn-default'
                }
                disabled={this.state.executing}
              >
                <i className="fa fa-home" aria-hidden="true" />
              </button>
              <button
                onClick={this.toggleLimits}
                title={
                  this.state.isLimited
                    ? I18n.t('gui.ide.unlockField')
                    : I18n.t('gui.ide.lockField')
                }
                className={
                  this.state.executing
                    ? 'menuButton btn btn-xs btn-default'
                    : this.state.isLimited
                    ? 'menuButton btn btn-xs btn-danger'
                    : 'menuButton btn btn-xs btn-success'
                }
              >
                <i
                  className={
                    this.state.isLimited ? 'fa fa-lock' : 'fa fa-unlock-alt'
                  }
                  aria-hidden="true"
                />
              </button>
              <button
                onClick={this.clearButtonClick}
                title={I18n.t('gui.ide.clearField')}
                className={
                  !this.state.executing
                    ? 'menuButton btn btn-xs btn-warning'
                    : 'menuButton btn btn-xs btn-default'
                }
                disabled={this.state.executing}
              >
                <i className="fa fa-times" aria-hidden="true" />
              </button>
              <GuideButton />
            </div>
          </div>
          <div className="row" style={{ height: 'calc(70% - 32px)' }}>
            <Projects
              user={this.props.user}
              loadProject={this.loadProject}
              show={this.state.showProjectModal}
              close={this.toggleProjectModal}
            />

            {this.state.showSaveDialog && (
              <SaveDialog
                show={this.state.showSaveDialog}
                close={this.toggleSaveDialog}
                saveProject={this.saveProject}
              />
            )}

            {this.props.currentProject ? (
              <ShareDialog
                show={this.state.showShareDialog}
                close={this.toggleShareModal}
              />
            ) : (
              ''
            )}

            {this.props.pending && this.props.pending.length !== 0 ? (
              <PendingDialog
                show={this.state.showPendingDialog}
                close={this.togglePendingModal}
              />
            ) : (
              ''
            )}

            <Toolbox
              toggled={this.state.toggled}
              openModal={this.toggleProjectModal}
            />
            <div
              className={this.state.toggled ? 'col-xs-9' : 'col-xs-12'}
              style={{
                height: '100%',
                overflow: 'auto',
              }}
            >
              <AceEditor
                mode="python"
                theme="xcode"
                enableBasicAutocompletion
                enableLiveAutocompletion
                setOptions={{
                  tabSize: 2,
                  fontSize: 16,
                  wrap: false,
                  useSoftTabs: false,
                  showPrintMargin: false,
                  autoScrollEditorIntoView: true,
                  maxLines: 1000,
                  minLines: 35,
                  vScrollBarAlwaysVisible: false,
                }}
                onChange={this.onChange}
                value={this.state.code}
                name="editor"
                ref="ace"
                editorProps={{ $blockScrolling: Infinity }}
                key="code"
                readOnly={this.state.executing}
                style={{
                  width: '100%',
                  height: '100%',
                  backgroundColor: this.state.executing ? '#FFFAE8' : '',
                }}
              />
            </div>
          </div>

          <div
            className="row"
            style={{
              display: 'flex',
              flexDirection: 'row',
              flexWrap: 'nowrap',
              padding: '12px',
              height: '30%',
              backgroundColor: '#F2F2F2',
            }}
          >
            <div style={{ flexGrow: 99 }}>
              <AceEditor
                style={{ width: '100%' }}
                mode="python"
                theme="xcode"
                height="100%"
                ref="console"
                setOptions={{
                  tabSize: 2,
                  fontSize: 16,
                  wrap: false,
                  useSoftTabs: false,
                  showPrintMargin: false,
                  showGutter: false,
                }}
                name="console"
                value={this.outText.join('')}
                editorProps={{ $blockScrolling: Infinity }}
                key="output"
                readOnly
              />
            </div>
            <div
              style={{ marginLeft: '12px', maxWidth: '24px', height: '100%' }}
            >
              <button
                onClick={this.clearConsole}
                className={
                  !this.state.executing
                    ? 'btn-xs btn-warning'
                    : 'btn-xs btn-default'
                }
                title={I18n.t('gui.ide.consoleClear')}
                disabled={this.state.executing}
              >
                &#9114;
              </button>
            </div>
          </div>

          <Script
            url="https://progkids.ams3.cdn.digitaloceanspaces.com/web/skulpt/skulpt.min.js"
            onLoad={this.handleLoadMin}
          />
          {this.state.loadedMin && (
            <Script
              url="https://progkids.ams3.cdn.digitaloceanspaces.com/web/skulpt/skulpt-stdlib.js"
              onLoad={this.prepareSkulpt}
            />
          )}
        </div>
      </div>
    );
  }
}

Ace.propTypes = {
  switchCourse: PropTypes.func.isRequired,
  saveProject: PropTypes.func.isRequired,
  loadProject: PropTypes.func.isRequired,
};

function mapStateToProps(state) {
  const { current: currentProject, pending } = state.projects;

  return {
    user: state.auth.user,
    socket: state.socket.current,
    isUserOnline: state.utils.isUserOnline,
    currentProject,
    pending,
  };
}

const mapDispatchToProps = dispatch => ({
  showLauncherGuide: () => {
    dispatch(setLauncherGuideVisibility(true));
  },
  switchCourse: () => {
    dispatch(getCourse('code'));
  },
  finishLoad: () => {
    dispatch(finishLoad('code'));
  },
  saveProject: project => {
    dispatch(saveProject(project));
  },
  loadProject: project => {
    dispatch(receiveProject(project));
  },
  clearProject: () => {
    dispatch(receiveProject(null));
  },
});

export default connect(
  mapStateToProps,
  mapDispatchToProps
)(Ace);
